import React, {useState, useEffect} from "react";
import { Box, useTheme, useMediaQuery } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useGetAllActivitiesQuery } from "../../state/api";
import Header from "components/Header";
import FlexBetween from "components/FlexBetween";
import DataGridCustomToolbar from "components/DataGridCustomToolbar";

const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

const formatDate = (timestamp) => {
        if (!timestamp) return '';

        const date = new Date(timestamp * 1000);
        return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        });
    };

const Activities = () => {
    const theme = useTheme();
    const isNonMediumScreens = useMediaQuery("(min-width: 1200px)");
    const { data: activitiesData, isLoading, error } = useGetAllActivitiesQuery();
    // Log error for debugging
useEffect(() => {
    if (error) {
        console.error("Error fetching activities:", error);
    }
}, [error]);
        
    const activities = activitiesData?.activities || [];
    const total = activitiesData?.total || 0;
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchInput, setSearchInput] = useState("");
    const debouncedSearchInput = useDebounce(searchInput, 500);
    const filteredActivities = activities.filter(activity =>
    (activity.name?.toLowerCase() || "").includes(debouncedSearchInput.toLowerCase())
    );

    const columns = [
        {
        field: "id",
        headerName: "ID",
        minWidth: 50,
        flex: isMobile ? 0.2 : 0.3,
        },
        {
        field: "type",
        headerName: "Type",
        minWidth: 50,
        flex: isMobile ? 0 : 0.3,
        },
        {
        field: "title",
        headerName: "Title",
        minWidth: 180,
        flex: isMobile ? 1 : 1.2,
        },
        {
        field: "dueDate",
        headerName: "Date",
        minWidth: 50,
        flex: isMobile ? 0 : 0.5,
        valueFormatter: (params) => formatDate(params.value),
        },
        {
        field: "completedAt",
        headerName: "Completed at",
        minWidth: 50,
        flex: isMobile ? 0 : 0.5,
        valueFormatter: (params) => formatDate(params.value),
        },
        {
        field: "description",
        headerName: "Description",
        minWidth: 100,
        flex: isMobile ? 0 : 0.8,
        },
        {
        field: "lastModifiedAt",
        headerName: "Last Modify",
        minWidth: 60,
        flex: isMobile ? 0 : 0.5,
        valueFormatter: (params) => formatDate(params.value),
        },
        {
        field: "tags",
        headerName: "Tags",
        minWidth: 50,
        flex: isMobile ? 0 : 0.5,
        },
    ];

    return (
        <Box m="1.5rem 2.5rem">
        <FlexBetween>
            <Header title="ACTIVITIES" subtitle="Salesmate Activities" />
            <Box>
            </Box>
        </FlexBetween>

        <Box
            mt="20px"
            display="grid"
            gridTemplateColumns="repeat(12, 1fr)"
            gridAutoRows="160px"
            gap="20px"   
            sx={{
            "& > div": { gridColumn: isNonMediumScreens ? undefined : "span 12" },
            }}
        >
            <Box
            height={750} 
            gridColumn="span 12"
            gridRow="span 3"
            sx={{
                "& .MuiDataGrid-root": {
                border: "none",
                borderRadius: "5rem",
                },
                "& .MuiDataGrid-cell": {
                borderBottom: "none",
                width: '100%',
                },
                "& .MuiDataGrid-columnHeaders": {
                backgroundColor: theme.palette.background.alt,
                color: theme.palette.secondary[300],
                borderBottom: "none",
                },
                "& .MuiDataGrid-virtualScroller": {
                backgroundColor: theme.palette.background.alt,
                },
                "& .MuiDataGrid-footerContainer": {
                backgroundColor: theme.palette.background.alt,
                color: theme.palette.secondary[100],
                borderTop: "none",
                },
                "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                color: `${theme.palette.secondary[200]} !important`,
                },
            }}
            >
            <DataGrid
                loading={isLoading || !activitiesData}
                getRowId={(activity) => activity.id}
                rows={filteredActivities}
                columns={columns}
                rowCount={total}
                components={{
                    Toolbar: () => (
                        <DataGridCustomToolbar
                        setSearchInput={setSearchInput}
                        />
                    )
                }}
            />
            
            </Box>
        </Box>
        </Box>
    );
};

export default Activities;